/**
 * Formats an amount with currency symbol using Intl.NumberFormat
 * (e.g., "$400.00" for USD, "Nu.200.50" for BTN, "€400.00" for EUR)
 *
 * @param amount - The amount to format (number, string, or null/undefined)
 * @param currency - The ISO currency code (e.g., "USD", "BTN", "EUR"). Defaults to "USD" if not provided
 * @param locale - The locale to use for formatting. Defaults to "en-US"
 * @returns Formatted string with currency symbol (e.g., "$400.00") or "-" if amount is invalid
 *
 * @example
 * formatAmount(400, "USD") // "$400.00"
 * formatAmount(200.5, "BTN") // "Nu.200.50" (or "BTN 200.50" depending on locale)
 * formatAmount(400, "EUR", "en-US") // "€400.00"
 * formatAmount(null) // "-"
 */
export const formatAmount = (
  amount: number | string | null | undefined,
  currency: string | null | undefined = 'USD',
  locale: string = 'en-US'
): string => {
  if (amount === null || amount === undefined) return '-';

  const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));

  if (isNaN(numAmount)) return '-';

  const currencyCode = currency || 'USD';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  } catch {
    // Fallback to original format if currency code is invalid
    return `${numAmount.toFixed(2)} ${currencyCode}`;
  }
};
